import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, Box, Grid, styled } from '@mui/material';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { ColorPalette } from 'shared/consts/colorPalette';
import { InfoContainer } from 'widgets/CoUsers/ui/CoUserDetailsInfo/ui/Paper';
import { SubscriptionsTable } from 'widgets/admin/UserDetails/ui/tabs/Subscriptions/SubscriptionsTable';
import { getUserSubscriptions } from 'entities/admin/Users/api';
import { capitalizeFirstLetter } from 'shared/helpers/capitalizeFirstLetter';
import { getMaxValue, getInfOrValue } from 'shared/helpers/getMaxValue';
import { STypesEnum } from 'entities/Subscriptions/enums';
import { SubscriptionType } from 'entities/Subscriptions/types';
import LimitPages from './limit-pages';

const Subscriptions: FC = () => {
	const { userId } = useParams();
	const { t } = useTranslation();
	const [currentSubscription, setCurrentSubscription] = useState<SubscriptionType | null>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any

	const { data } = useQuery({
		queryKey: [`admin/users/${userId}/subscriptions`],
		queryFn: () => getUserSubscriptions(userId),
		onSuccess: (response) => {
			const currentSub = response.data?.find((item: any) => item.isCurrentSubscription);

			setCurrentSubscription(currentSub);
		},
	});

	return (
		<>
			<Typography variant={'h5'} color={ColorPalette.astronaut} sx={{ margin: '44px 0' }}>
				{t('subscriptions.title')}
			</Typography>

			<InfoContainer sx={{ width: '100%' }}>
				<Typography variant={'h5'} color={ColorPalette.astronaut} sx={{ marginBottom: '44px' }}>
					{t('subscriptions.tabs.tab_1')}
				</Typography>

				{currentSubscription ? (
					<Grid container sx={{ gap: '20px' }}>
						<Grid size={5}>
							<Box
								sx={{
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
								}}
							>
								<Typography variant={'h3'} color={ColorPalette.royalBlue}>
									{currentSubscription?.status === 'canceled'
										? t('subscriptions.canceled')
										: capitalizeFirstLetter(currentSubscription?.subscriptionType || '')}
								</Typography>
								{currentSubscription.subscriptionType === STypesEnum.UNLIMITED && (
									<LimitPages subscription={currentSubscription} />
								)}
							</Box>
						</Grid>
						<Grid size={2}>
							<StyledBox>
								<Typography variant={'body2'}>
									{t('admin.users.details.tab_2.sub_cost')}
								</Typography>
								<Typography variant={'body2'} color={ColorPalette.rockBlue}>
									{currentSubscription?.subscriptionCost
										? `${currentSubscription?.subscriptionCost / 100} €`
										: '0'}
								</Typography>
							</StyledBox>
						</Grid>
						<Grid size={2} sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
							<StyledBox>
								<Typography variant={'body2'}>
									{t('admin.users.details.tab_2.trial_ends')}
								</Typography>
								<Typography variant={'body2'} color={ColorPalette.rockBlue}>
									{format(new Date(currentSubscription.currentPeriodEnd), 'dd/MM/yyyy')}
								</Typography>
							</StyledBox>
							<StyledBox>
								<Typography variant={'body2'}>
									{t('admin.users.details.tab_2.sub_bought')}
								</Typography>
								<Typography variant={'body2'} color={ColorPalette.rockBlue}>
									{format(new Date(currentSubscription.currentPeriodStart), 'dd/MM/yyyy')}
								</Typography>
							</StyledBox>
						</Grid>
						<Grid size={2} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
							<StyledBox>
								<Typography variant={'body2'}>{t('shared_texts.pages_imported')}</Typography>
								<Typography
									variant={'body2'}
									color={
										currentSubscription.maxPagesWarningLimit &&
										currentSubscription.maxPagesWarningLimit <
											currentSubscription.totalPages
											? 'error'
											: ColorPalette.rockBlue
									}
								>
									{currentSubscription.subscriptionType === STypesEnum.UNLIMITED
										? `${currentSubscription.totalPages}/${getInfOrValue(
												currentSubscription.maxPagesWarningLimit,
										  )}`
										: `${currentSubscription?.totalFiles}/${
												currentSubscription?.maxFiles === 'inf'
													? '∞'
													: currentSubscription?.maxFiles
										  }`}
								</Typography>
							</StyledBox>
							<StyledBox>
								<Typography variant={'body2'}>
									{t('admin.users.details.tab_2.users_added')}
								</Typography>
								<Typography variant={'body2'} color={ColorPalette.rockBlue}>
									{`${currentSubscription?.totalCousers}/${getMaxValue(
										currentSubscription.maxCousers,
										currentSubscription.maxFreeCousers,
									)}`}
								</Typography>
							</StyledBox>
						</Grid>
					</Grid>
				) : (
					'-'
				)}
			</InfoContainer>

			<Typography variant={'h6'} color={ColorPalette.astronaut} sx={{ margin: '44px 0' }}>
				{t('admin.users.details.subscriptions_history')}
			</Typography>

			<SubscriptionsTable subscriptions={data?.data || []} />
		</>
	);
};

export default Subscriptions;

const StyledBox = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 1.5;
`;
